// 📄 src/components/Hero.js
import React from 'react';
import { motion } from 'framer-motion';
import { ChevronRight } from 'lucide-react';
import { Link } from 'react-router-dom';
import tacoma33 from '../assets/tacoma33.webp';
import instalation2 from '../assets/instalation2.jpeg';
import BannerRed from "../assets/bannerred.png";
import { Swiper, SwiperSlide } from 'swiper/react';
import { Autoplay, Pagination } from 'swiper/modules'; // ✅ include Autoplay module
import 'swiper/css';
import 'swiper/css/pagination';
import './HeroCarousel.css';

const Hero = () => {
  // ✅ Desktop Hero (Modernized)
  const renderDesktopHero = () => {
    return (
      <>
        <div
          className="relative h-screen overflow-hidden flex-col hidden md:block group"
          role="region"
          aria-label="Hero slide showing Tacoma parts and install services"
        >
          {/* Left image */}
          <motion.div
            className="absolute inset-0 z-10 overflow-hidden"
            style={{ clipPath: 'polygon(0 0, 65% 0, 35% 100%, 0% 100%)' }}
          >
            <motion.img
              src={tacoma33}
              alt="Tacoma truck showcasing parts"
              className="w-full h-full object-cover"
              loading="lazy"
              initial={{ scale: 1.1 }}
              whileHover={{ scale: 1 }}
              transition={{ duration: 0.6, ease: "easeOut" }}
            />
            <div className="absolute inset-0 bg-gradient-to-r from-black/85 via-black/60 to-transparent" />
            <div className="absolute inset-0 bg-gradient-to-b from-transparent via-transparent to-black/40" />
          </motion.div>

          {/* Right image */}
          <motion.div
            className="absolute inset-0 z-0 overflow-hidden"
            style={{ clipPath: 'polygon(100% 0, 100% 100%, 35% 100%, 65% 0)' }}
          >
            <motion.img
              src={instalation2}
              alt="Install services promotional truck"
              className="w-full h-full object-cover"
              loading="lazy"
              initial={{ scale: 1.1 }}
              whileHover={{ scale: 1 }}
              transition={{ duration: 0.6, ease: "easeOut" }}
            />
            <div className="absolute inset-0 bg-gradient-to-l from-black/85 via-black/60 to-transparent" />
            <div className="absolute inset-0 bg-gradient-to-b from-transparent via-transparent to-black/40" />
          </motion.div>

          {/* Left side */}
          <div className="absolute left-0 top-0 w-1/2 h-full flex items-center justify-center z-20 cursor-pointer group/left">
            <motion.div
              className="text-center text-white max-w-lg mx-auto px-8"
              initial={{ opacity: 0, y: 40, x: -20 }}
              animate={{ opacity: 1, y: 0, x: 0 }}
              transition={{ duration: 0.8, ease: "easeOut" }}
            >
              <motion.div
                initial={{ opacity: 0, scale: 0.9 }}
                animate={{ opacity: 1, scale: 1 }}
                transition={{ duration: 0.6, delay: 0.2 }}
                className="mb-6"
              >
                <h1 className="text-5xl sm:text-6xl lg:text-7xl font-display font-extrabold mb-6 drop-shadow-2xl leading-tight tracking-tight">
                  Tacoma
                  <br />
                  <span className="text-brandred">Parts</span>
                </h1>
              </motion.div>
              <motion.p
                className="text-xl sm:text-2xl mb-10 text-white/90 font-medium leading-relaxed"
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: 0.4 }}
              >
                Premium performance parts for every generation of Tacoma
              </motion.p>
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: 0.6 }}
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
              >
                <Link
                  to="/shop"
                  className="
                    hero-shop-now
                    inline-flex
                    items-center justify-center
                    bg-brandred
                    text-offwhite
                    font-bold
                    text-lg sm:text-xl
                    px-10 py-4 sm:px-12 sm:py-5
                    rounded-xl
                    shadow-2xl
                    hover:bg-[#E63B3B]
                    hover:shadow-[0_10px_40px_rgba(220,38,38,0.5)]
                    transition-all duration-300
                    relative
                    z-30
                    group/button
                    backdrop-blur-sm
                  "
                >
                  <span className="mr-2">Shop Now</span>
                  <motion.div
                    animate={{ x: [0, 4, 0] }}
                    transition={{ duration: 1.5, repeat: Infinity, ease: "easeInOut" }}
                    className="group-hover/button:translate-x-1 transition-transform"
                  >
                    <ChevronRight size={22} />
                  </motion.div>
                </Link>
              </motion.div>
            </motion.div>
          </div>

          {/* Right side */}
          <div className="absolute right-0 bottom-0 w-1/2 h-full flex items-center justify-center z-20 cursor-pointer group/right">
            <motion.div
              className="text-center text-white max-w-lg mx-auto px-8"
              initial={{ opacity: 0, y: 40, x: 20 }}
              animate={{ opacity: 1, y: 0, x: 0 }}
              transition={{ duration: 0.8, delay: 0.2, ease: "easeOut" }}
            >
              <motion.div
                initial={{ opacity: 0, scale: 0.9 }}
                animate={{ opacity: 1, scale: 1 }}
                transition={{ duration: 0.6, delay: 0.4 }}
                className="mb-6"
              >
                <h1 className="text-5xl sm:text-6xl lg:text-7xl font-display font-extrabold mb-6 drop-shadow-2xl leading-tight tracking-tight">
                  Install
                  <br />
                  <span className="text-yellow-300">Services</span>
                </h1>
              </motion.div>
              <motion.p
                className="text-xl sm:text-2xl mb-10 text-white/90 font-medium leading-relaxed"
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: 0.6 }}
              >
                Toyota installation specialists
              </motion.p>
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: 0.8 }}
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
              >
                <Link
                  to="/contact?inquiry=installation"
                  className="
                    inline-flex
                    items-center justify-center
                    bg-brandred
                    text-offwhite
                    font-bold
                    text-lg sm:text-xl
                    px-10 py-4 sm:px-12 sm:py-5
                    rounded-xl
                    shadow-[0_10px_40px_rgba(220,38,38,0.5)]
                    border-2 border-brandred
                    hover:bg-[#E63B3B]
                    hover:shadow-[0_15px_50px_rgba(220,38,38,0.6)]
                    transition-all duration-300
                    group/button
                    relative
                    z-30
                  "
                >
                  <span className="mr-2">Book Install</span>
                  <motion.div
                    animate={{ x: [0, 4, 0] }}
                    transition={{ duration: 1.5, repeat: Infinity, ease: "easeInOut" }}
                    className="group-hover/button:translate-x-1 transition-transform"
                  >
                    <ChevronRight size={22} className="text-offwhite" />
                  </motion.div>
                </Link>
              </motion.div>
            </motion.div>
          </div>
        </div>
      </>
    );
  };

  // ✅ Mobile Hero (auto-scroll + big text)
  const renderMobileHero = () => {
    return (
      <>
        <Swiper
          slidesPerView={1}
          loop={false}
          pagination={{ clickable: true }}
          autoplay={{ delay: 3000, disableOnInteraction: false }}
          modules={[Autoplay, Pagination]}
          className="w-full"
        >
          {/* Slide 1 - Tacoma Parts */}
          <SwiperSlide>
            <div className="relative w-full h-screen overflow-hidden">
              <img
                src={tacoma33}
                alt="Tacoma truck showcasing parts"
                className="w-full h-full object-cover"
                loading="lazy"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-black/85 via-black/50 to-black/30" />
              <div className="absolute inset-0 flex flex-col items-center justify-center text-center px-6">
                <motion.h2
                  className="text-6xl sm:text-7xl font-extrabold mb-6 text-white drop-shadow-2xl leading-tight tracking-tight"
                  initial={{ opacity: 0, y: 30 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.6 }}
                >
                  Tacoma
                  <br />
                  <span className="text-brandred">Parts</span>
                </motion.h2>
                <motion.p
                  className="text-xl sm:text-2xl font-medium mb-10 text-white/90 max-w-md leading-relaxed"
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.6, delay: 0.2 }}
                >
                  Premium performance parts for every generation of Tacoma
                </motion.p>
                {/* Button visible on all mobile sizes */}
                <motion.div
                  className="flex justify-center items-center"
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.6, delay: 0.4 }}
                  whileHover={{ scale: 1.05 }}
                  whileTap={{ scale: 0.95 }}
                >
                  <Link
                    to="/shop"
                    className="bg-brandred text-offwhite font-bold text-base sm:text-lg px-8 sm:px-10 py-3 sm:py-4 rounded-xl shadow-2xl hover:bg-[#E63B3B] hover:shadow-[0_10px_40px_rgba(220,38,38,0.5)] transition-all duration-300 flex items-center gap-2 mx-auto inline-flex"
                  >
                    Shop Now
                    <ChevronRight size={20} className="sm:w-6 sm:h-6" />
                  </Link>
                </motion.div>
              </div>
            </div>
          </SwiperSlide>

          {/* Slide 2 - Install Services */}
          <SwiperSlide>
            <div className="relative w-full h-screen overflow-hidden">
              <img
                src={instalation2}
                alt="Install services promotional truck"
                className="w-full h-full object-cover"
                loading="lazy"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-black/85 via-black/50 to-black/30" />
              <div className="absolute inset-0 flex flex-col items-center justify-center text-center px-6">
                <motion.h2
                  className="text-6xl sm:text-7xl font-extrabold mb-6 text-white drop-shadow-2xl leading-tight tracking-tight"
                  initial={{ opacity: 0, y: 30 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.6 }}
                >
                  Install
                  <br />
                  <span className="text-yellow-300">Services</span>
                </motion.h2>
                <motion.p
                  className="text-xl sm:text-2xl font-medium mb-10 text-white/90 max-w-md leading-relaxed"
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.6, delay: 0.2 }}
                >
                  Professional installation by Toyota specialists
                </motion.p>
                {/* Button visible on all mobile sizes */}
                <motion.div
                  className="flex justify-center items-center"
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.6, delay: 0.4 }}
                  whileHover={{ scale: 1.05 }}
                  whileTap={{ scale: 0.95 }}
                >
                  <Link
                    to="/contact?inquiry=installation"
                    className="bg-brandred text-offwhite font-bold text-base sm:text-lg px-8 sm:px-10 py-3 sm:py-4 rounded-xl shadow-[0_10px_40px_rgba(220,38,38,0.5)] hover:bg-[#E63B3B] hover:shadow-[0_15px_50px_rgba(220,38,38,0.6)] transition-all duration-300 flex items-center gap-2 mx-auto inline-flex border-2 border-brandred"
                  >
                    Book Install
                    <ChevronRight size={20} className="sm:w-6 sm:h-6 text-offwhite" />
                  </Link>
                </motion.div>
              </div>
            </div>
          </SwiperSlide>
        </Swiper>
      </>
    );
  };

  return (
    <>
      <section
        id="home"
        className="relative min-h-screen overflow-visible bg-[#0F0F0F] flex flex-col"
      >
        {/* Desktop */}
        <div className="hidden md:block w-full h-full">{renderDesktopHero()}</div>

        {/* Mobile */}
        <div className="block md:hidden w-full">{renderMobileHero()}</div>
      </section>

      {/* --- Red Banner Section (Enhanced) --- */}
      <motion.div
        className="
          hero-banner
          w-full
          py-12 md:py-16
          flex flex-col
          items-center justify-center
          bg-cover bg-center
          relative
          overflow-hidden
        "
        style={{ backgroundImage: `url(${BannerRed})` }}
        initial={{ opacity: 0 }}
        whileInView={{ opacity: 1 }}
        viewport={{ once: true, margin: "-100px" }}
        transition={{ duration: 0.8, ease: "easeOut" }}
      >
        {/* Subtle dark overlay for better text contrast */}
        <div className="absolute inset-0 bg-black/20 pointer-events-none" />
        
        {/* Decorative corner accents */}
        <div className="absolute top-4 left-4 w-16 h-16 border-t-2 border-l-2 border-white/30 hidden md:block" />
        <div className="absolute top-4 right-4 w-16 h-16 border-t-2 border-r-2 border-white/30 hidden md:block" />
        <div className="absolute bottom-4 left-4 w-16 h-16 border-b-2 border-l-2 border-white/30 hidden md:block" />
        <div className="absolute bottom-4 right-4 w-16 h-16 border-b-2 border-r-2 border-white/30 hidden md:block" />

        <div className="relative z-10 max-w-4xl mx-auto px-4 text-center">
          <motion.h2
            className="text-3xl md:text-5xl lg:text-6xl font-extrabold text-white drop-shadow-2xl mb-4 tracking-tight text-center leading-tight"
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6, delay: 0.2, ease: "easeOut" }}
          >
            Explore Our Best-Selling
            <br />
            <span className="text-yellow-300 drop-shadow-lg">Tacoma Upgrades</span>
          </motion.h2>

          <motion.p
            className="text-white/90 text-lg md:text-xl mb-8 max-w-2xl mx-auto font-medium"
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6, delay: 0.3, ease: "easeOut" }}
          >
            Premium parts, expert installation, and fast shipping nationwide
          </motion.p>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6, delay: 0.4, ease: "easeOut" }}
            whileHover={{ scale: 1.05 }}
            whileTap={{ scale: 0.95 }}
          >
            <Link
              to="/shop"
              className="
                hero-banner-button
                inline-flex
                items-center justify-center
                bg-white
                text-black
                font-bold
                text-base md:text-lg
                px-8 md:px-10
                py-3 md:py-4
                rounded-full
                shadow-2xl
                hover:bg-gray-50
                hover:shadow-[0_10px_30px_rgba(0,0,0,0.3)]
                transition-all duration-300
                relative
                z-30
                group
              "
            >
              <span className="mr-2">Browse Products</span>
              <motion.div
                animate={{ x: [0, 5, 0] }}
                transition={{ duration: 1.5, repeat: Infinity, ease: "easeInOut" }}
                className="group-hover:translate-x-1 transition-transform"
              >
                <ChevronRight size={20} />
              </motion.div>
            </Link>
          </motion.div>
        </div>
      </motion.div>
      {/* --- End Red Banner Section (Enhanced) --- */}
    </>
  );
};

export default Hero;